import toast from "react-hot-toast";

const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/**
 * Validate a form payload against a schema before sending it to the backend.
 *
 * Schema shape (one entry per field):
 *   field: {
 *     required: boolean | ((data) => boolean),
 *     oneOf: ["a", "b"],            // at least one of these must be filled
 *     email: true,
 *     min: number,
 *     match: "otherField",          // must equal data[otherField]
 *     validate: (value, data) => boolean,
 *     label: "Human readable name",
 *   }
 *
 * @returns {string[]} list of invalid field labels (empty when valid)
 */
export function validate(data, schema) {
  const errors = [];

  for (const field of Object.keys(schema)) {
    const rule = schema[field];
    const value = data?.[field];
    const label = rule.label || field;

    const required =
      typeof rule.required === "function" ? rule.required(data) : rule.required;

    if (required && isEmpty(value)) {
      errors.push(label);
      continue;
    }

    if (rule.oneOf && !rule.oneOf.some((key) => !isEmpty(data?.[key]))) {
      errors.push(label);
      continue;
    }

    if (isEmpty(value)) continue;

    if (rule.email && !/^[^\s@]+@[^\s@]+$/.test(String(value))) {
      errors.push(label);
      continue;
    }

    if (rule.min && String(value).length < rule.min) {
      errors.push(label);
      continue;
    }

    if (rule.match && value !== data?.[rule.match]) {
      errors.push(label);
      continue;
    }

    if (typeof rule.validate === "function" && !rule.validate(value, data)) {
      errors.push(label);
    }
  }

  return errors;
}

/**
 * Validate and surface the first error as a toast. Returns true when valid.
 */
export function validateOrToast(data, schema, title = "Please fill all fields") {
  const errors = validate(data, schema);
  if (errors.length > 0) {
    toast.error(`${title}: ${errors.join(", ")}`);
    return false;
  }
  return true;
}
